export type RoleScopeRole =
  | "SuperAdmin"
  | "OrganizationAdmin"
  | "LeagueAdmin"
  | "Commish"
  | "Coach"
  | "Evaluator"
  | "Parent";

export type RoleScopeTarget = "global" | "organization" | "league" | "team" | "parent";

export type ActiveRoleScope = {
  id: string;
  role: RoleScopeRole;
  targetType: RoleScopeTarget;
  label: string;
  organizationId?: string;
  leagueId?: string;
  teamId?: string;
  playerIds?: string[];
};

export type OrganizationLike = {
  id: string;
  name: string;
  admins?: Array<string | null> | null;
};

export type LeagueLike = {
  id: string;
  name: string;
  organizationId?: string | null;
  admins?: Array<string | null> | null;
};

export type TeamScopeLike = {
  id: string;
  name: string;
  organizationId?: string | null;
  leagueId?: string | null;
  coachUsernames?: Array<string | null> | null;
};

export type UserAssignmentScopeLike = {
  id: string;
  userId: string;
  organizationId?: string | null;
  leagueId?: string | null;
  teamId?: string | null;
  playerId?: string | null;
  relationship?: string | null;
};

export type PlayerParentScopeLike = {
  id: string;
  playerId: string;
  parentEmail?: string | null;
};

const normalizeRole = (role: string) => (role === "Parents" ? "Parent" : role);

const relationshipHas = (assignment: UserAssignmentScopeLike, keyword: string) =>
  (assignment.relationship ?? "").toLowerCase().includes(keyword);

const dedupeScopes = (scopes: ActiveRoleScope[]): ActiveRoleScope[] => {
  const seen = new Set<string>();
  return scopes.filter((scope) => {
    if (seen.has(scope.id)) {
      return false;
    }
    seen.add(scope.id);
    return true;
  });
};

export const buildRoleScopes = ({
  userRoles,
  username,
  email,
  organizations = [],
  leagues = [],
  teams = [],
  userAssignments = [],
  playerParents = []
}: {
  userRoles: string[];
  username: string;
  email?: string;
  organizations?: OrganizationLike[];
  leagues?: LeagueLike[];
  teams?: TeamScopeLike[];
  userAssignments?: UserAssignmentScopeLike[];
  playerParents?: PlayerParentScopeLike[];
}): ActiveRoleScope[] => {
  const roleSet = new Set(userRoles.map(normalizeRole));
  const scopes: ActiveRoleScope[] = [];

  if (roleSet.has("SuperAdmin")) {
    scopes.push({
      id: "SuperAdmin:global",
      role: "SuperAdmin",
      targetType: "global",
      label: "SuperAdmin - all organizations"
    });
  }

  if (roleSet.has("OrganizationAdmin") || roleSet.has("Commish")) {
    for (const organization of organizations) {
      const isAdminArrayMatch = (organization.admins ?? []).includes(username);
      const isAssignmentMatch = userAssignments.some(
        (assignment) =>
          assignment.userId === username &&
          assignment.organizationId === organization.id &&
          (relationshipHas(assignment, "commish") || relationshipHas(assignment, "admin") || !assignment.relationship)
      );
      if (!isAdminArrayMatch && !isAssignmentMatch && !roleSet.has("SuperAdmin")) {
        continue;
      }
      scopes.push({
        id: `OrganizationAdmin:organization:${organization.id}`,
        role: roleSet.has("OrganizationAdmin") ? "OrganizationAdmin" : "Commish",
        targetType: "organization",
        organizationId: organization.id,
        label: `Commissioner of ${organization.name}`
      });
    }
  }

  if (roleSet.has("LeagueAdmin") || roleSet.has("Commish")) {
    for (const league of leagues) {
      const isAdminArrayMatch = (league.admins ?? []).includes(username);
      const isAssignmentMatch = userAssignments.some(
        (assignment) =>
          assignment.userId === username &&
          assignment.leagueId === league.id &&
          (relationshipHas(assignment, "commish") || relationshipHas(assignment, "admin") || !assignment.relationship)
      );
      if (!isAdminArrayMatch && !isAssignmentMatch && !roleSet.has("SuperAdmin")) {
        continue;
      }
      scopes.push({
        id: `LeagueAdmin:league:${league.id}`,
        role: roleSet.has("LeagueAdmin") ? "LeagueAdmin" : "Commish",
        targetType: "league",
        leagueId: league.id,
        organizationId: league.organizationId ?? undefined,
        label: `Commissioner of ${league.name}`
      });
    }
  }

  if (roleSet.has("Coach")) {
    for (const team of teams) {
      const isCoachArrayMatch = (team.coachUsernames ?? []).includes(username);
      const isAssignmentMatch = userAssignments.some(
        (assignment) =>
          assignment.userId === username &&
          assignment.teamId === team.id &&
          (relationshipHas(assignment, "coach") || relationshipHas(assignment, "assistant") || !assignment.relationship)
      );
      if (!isCoachArrayMatch && !isAssignmentMatch && !roleSet.has("SuperAdmin")) {
        continue;
      }
      scopes.push({
        id: `Coach:team:${team.id}`,
        role: "Coach",
        targetType: "team",
        teamId: team.id,
        leagueId: team.leagueId ?? undefined,
        organizationId: team.organizationId ?? undefined,
        label: `Coach of ${team.name}`
      });
    }
  }

  if (roleSet.has("Evaluator")) {
    scopes.push({
      id: "Evaluator:global",
      role: "Evaluator",
      targetType: "global",
      label: "Evaluator - evaluation boards"
    });
  }

  const normalizedEmail = (email ?? "").trim().toLowerCase();
  const parentPlayerIds = normalizedEmail
    ? playerParents
        .filter((link) => (link.parentEmail ?? "").trim().toLowerCase() === normalizedEmail)
        .map((link) => link.playerId)
    : [];

  scopes.push({
    id: "Parent:parent",
    role: "Parent",
    targetType: "parent",
    playerIds: parentPlayerIds,
    label: parentPlayerIds.length > 1 ? `Parent - ${parentPlayerIds.length} players` : "Parent - my players"
  });

  return dedupeScopes(scopes);
};

export const getRoleScopeHomePath = (scope?: ActiveRoleScope | null): string => {
  if (!scope) {
    return "/admin/dashboard";
  }
  if (scope.targetType === "team" && scope.teamId) {
    return `/admin/teams/${scope.teamId}`;
  }
  if (scope.targetType === "league" && scope.leagueId) {
    return `/admin/leagues?leagueId=${encodeURIComponent(scope.leagueId)}`;
  }
  if (scope.targetType === "organization" && scope.organizationId) {
    return `/admin/organizations?organizationId=${encodeURIComponent(scope.organizationId)}`;
  }
  return "/admin/dashboard";
};

export const getScopedNavPath = (basePath: string, scope?: ActiveRoleScope | null): string => {
  if (!scope) {
    return basePath;
  }
  if (basePath === "/admin/teams" && scope.targetType === "team" && scope.teamId) {
    return `/admin/teams/${scope.teamId}`;
  }

  const params = new URLSearchParams();
  if (scope.teamId) {
    params.set("teamId", scope.teamId);
  }
  if (scope.leagueId) {
    params.set("leagueId", scope.leagueId);
  }
  if (scope.organizationId) {
    params.set("organizationId", scope.organizationId);
  }
  if (scope.targetType === "parent") {
    params.set("scope", "parent");
  }

  if (params.size === 0 || !basePath.startsWith("/admin/")) {
    return basePath;
  }
  return `${basePath}?${params.toString()}`;
};
